import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { useWindowSize } from 'usehooks-ts';

import { SHOW_MODAL_ON_INACTION_SECONDS } from '@src/env';

import MarketingModalLayout from './Layout';

const cookies = new Cookies();

export interface MarketingModalProps {
  buttonText?: string;
  customUrl?: string;
  disclosure?: string;
  guid: string;
  header: string;
  highlightedCta: string;
  id: number;
  isActiveUser?: boolean;
  isGdprLead?: boolean;
  modalType: 'standard_offer' | 'standard_subscription' | 'lead_capture' | 'custom_url';
  offerLink: string;
  subtitle: string;
  title: string;
  trackingProps: { [key: string]: string };
}

const updateWindowEvents = (value: () => void | null) => {
  window.onclick = value;
  window.onkeypress = value;
  window.ontouchstart = value;
  window.onmousemove = value;
  window.onmousedown = value;
  window.onscroll = value;
};

// Required for tests because window can't be resized to less than 500px
const MOBILE_BREAKPOINT = 500;

const MarketingModal = (props: MarketingModalProps) => {
  const { id, isActiveUser } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hasClosedModal, setHasClosedModal] = useState<boolean>(false);
  const { width } = useWindowSize();

  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const canShowModal = useCallback(() => {
    // only prevent modal from showing from cookie tracking for active users
    const preventShowModalFromCookies = isActiveUser && cookies.get(`marketing-modal-closed-${id}`);
    return !preventShowModalFromCookies && !hasClosedModal && !isOpen;
  }, [isActiveUser, hasClosedModal, isOpen, id]);

  const showModal = useCallback(() => {
    if (canShowModal()) {
      clearTimeout(timerRef.current);
      setIsOpen(true);
      updateWindowEvents(null);
      /* eslint-disable no-undef */
      // @ts-expect-error TODO: import ahoy instead of using global instance?
      trackEvent('Views', 'Marketing Modal view', {
        identifier: id,
        name: `marketing_modal`,
        page: window.location.pathname,
      });
    }

    /* eslint-enable no-undef */
  }, [id, canShowModal]);

  const closeModal = () => {
    // Server will look for this cookie; if set, it won't instantiate an modal
    cookies.set(`marketing-modal-closed-${id}`, true, { path: '/' });
    setIsOpen(false);
    setHasClosedModal(true);
  };

  const resetTime = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(showModal, SHOW_MODAL_ON_INACTION_SECONDS);
  }, [showModal]);

  // show modal when paywall is visible in viewport (for mobile only)
  useEffect(() => {
    const paywall = document.querySelector('[data-paywall]');

    const checkForPaywallVisibility = () => {
      const rect = paywall.getBoundingClientRect();
      if (rect.bottom <= window.innerHeight) {
        showModal();
      }
    };

    if (canShowModal() && paywall && width <= MOBILE_BREAKPOINT) {
      document.addEventListener('scroll', checkForPaywallVisibility);
    }

    return () => {
      document.removeEventListener('scroll', checkForPaywallVisibility);
    };
  }, [showModal, canShowModal, width]);

  useEffect(() => {
    if (canShowModal()) {
      resetTime();
      updateWindowEvents(resetTime);
      // show when mouse leaves page
      document.addEventListener('mouseleave', showModal);
    } else {
      document.removeEventListener('mouseleave', showModal);
    }

    return () => {
      document.removeEventListener('mouseleave', showModal);
    };
  }, [showModal, resetTime, canShowModal]);

  return (
    <BrowserRouter>
      <MarketingModalLayout {...props} isOpen={isOpen} closeModal={closeModal} />
    </BrowserRouter>
  );
};

export default MarketingModal;
